// src/pages/ReportingHub.jsx
import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { Link, useNavigate } from "react-router-dom";
import { FaChartBar, FaChevronRight } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faChartBar as faChartBarIcon,
  faUsers,
  faListAlt,
  faTicketAlt,
} from "@fortawesome/free-solid-svg-icons";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

/* ------------------------------ helpers ---------------------------------- */
function readUser() {
  try { return JSON.parse(localStorage.getItem("user")) || {}; } catch { return {}; }
}
async function toJsonSafe(res) {
  const t = await res.text();
  try { return JSON.parse(t); } catch { throw new Error(t.slice(0, 240)); }
}
const toneStyle = (s) => {
  const k = String(s || "new").toLowerCase();
  if (k === "approved" || k === "pending") return { background: "rgba(16,185,129,0.15)", color: "#6ee7b7" };
  if (k === "rejected") return { background: "rgba(239,68,68,0.15)", color: "#fca5a5" };
  return { background: "rgba(56,189,248,0.15)", color: "#bae6fd" };
};

// leader/creator SLIIT ID
const pickLeaderSliitId = (p) =>
  p.created_by_sliitid || p.project_officer_sliitid || p.created_by || p.owner || "";

/** SUPER robust phase resolver with default of 1 (matches your DB default). */
function getPhaseCount(p) {
  // direct candidates (common API keys)
  const directKeys = [
    "phase_count", "PhaseCount", "phaseCount",
    "current_phase", "currentPhase",
    "phase", "total_phases", "totalPhases",
  ];

  for (const key of directKeys) {
    if (p[key] !== undefined && p[key] !== null && p[key] !== "") {
      const n = Number(p[key]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
  }

  // nested shapes (e.g., meta.phase_count, details.phase_count)
  const nests = ["meta", "details", "data", "extra"];
  for (const root of nests) {
    const obj = p[root];
    if (obj && typeof obj === "object") {
      for (const key of directKeys) {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
          const n = Number(obj[key]);
          if (Number.isFinite(n) && n >= 0) return n;
        }
      }
    }
  }

  // array of phases
  if (Array.isArray(p.phases)) return p.phases.length;

  // FINAL fallback: your DB default is 1
  return 1;
}

/** case-insensitive map of { id: name } */
function normalizeNameMap(rawMap = {}) {
  const out = {};
  for (const [id, name] of Object.entries(rawMap)) {
    const s = String(id);
    out[s] = name;
    out[s.toUpperCase()] = name;
    out[s.toLowerCase()] = name;
    out[s.trim()] = name;
  }
  return out;
}

/* -------------------------------- Component ------------------------------- */
export default function ReportingHub() {
  const navigate = useNavigate();
  const userRaw = useMemo(() => readUser(), []);
  const user = userRaw?.name ? userRaw : { name: "Unknown User", role: "Member", image: "/user.png" };
  const isAdmin = /admin/i.test(String(user?.role || "")) || user?.is_admin === 1 || user?.isAdmin === 1;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);

  const [nameMap, setNameMap] = useState({});
  const [nameLoading, setNameLoading] = useState(false);

  /* ------------------------------- load projects -------------------------- */
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        setErr("");
        setLoading(true);
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_all.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            SLIITID: user?.SLIITID || null,
            include_roles: true,
            admin_mode: isAdmin ? 1 : 0,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await toJsonSafe(res);
        const list = Array.isArray(data?.projects) ? data.projects : [];

        const toneOrder = (s) => {
          const k = String(s || "").toLowerCase();
          if (k === "approved") return 0;
          if (k === "pending") return 1;
          if (k === "new") return 2;
          if (k === "rejected") return 3;
          return 4;
        };
        list.sort((a, b) => {
          const ta = toneOrder(a.status), tb = toneOrder(b.status);
          if (ta !== tb) return ta - tb;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });

        if (!off) setProjects(list);
      } catch (e) {
        if (!off) { setErr("Unable to fetch projects"); setProjects([]); }
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => { off = true; };
  }, [user?.SLIITID, isAdmin]);

  /* ------------- resolve SLIIT IDs -> human names ------------------------ */
  useEffect(() => {
    const ids = Array.from(
      new Set(projects.map((p) => String(pickLeaderSliitId(p) || "").trim()).filter(Boolean))
    ).filter((id) => !(id in nameMap));
    if (ids.length === 0) return;

    let off = false;
    (async () => {
      try {
        setNameLoading(true);
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/memberbasic_api.php?action=names_by_ids`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        });
        const data = await toJsonSafe(res);
        if (data?.success && data?.data && typeof data.data === "object") {
          if (!off) setNameMap((m) => ({ ...m, ...normalizeNameMap(data.data) }));
        }
      } catch {
        // ignore
      } finally {
        if (!off) setNameLoading(false);
      }
    })();

    return () => { off = true; };
  }, [projects, nameMap]);

  /* ----------------------------------- UI --------------------------------- */
  return (
    <div style={{ display: "flex", background: "#0b1221", color: "#fff", minHeight: "100vh" }}>
      <Sidebar />
      <Topbar user={user} />

      <main style={{ marginLeft: "270px", marginTop: "70px", width: "100%", padding: "30px" }}>
        {/* Header */}
        <div
          style={{
            background: "#111b31",
            border: "1px solid rgba(147,197,253,0.16)",
            borderRadius: "16px",
            padding: "18px 25px",
            marginBottom: "25px",
            display: "flex",
            alignItems: "center",
            boxShadow: "0 10px 25px -10px rgba(0,0,0,0.6)",
          }}
        >
          <div
            style={{
              background: "rgba(96,165,250,0.9)",
              borderRadius: "50%",
              padding: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "15px",
            }}
          >
            <FaChartBar style={{ fontSize: "20px", color: "#fff" }} />
          </div>
          <span style={{ fontSize: "18px", fontWeight: "700" }}>
            Club Dashboard <span style={{ color: "#94a3b8", fontWeight: "400" }}>/ Reporting Hub</span>
          </span>
        </div>

        {/* <div style={{ marginBottom: "30px", display: "flex", flexWrap: "wrap", gap: "12px" }}>
          {[
            { to: "/club-statistics", label: "Club Statistics", icon: faChartPie, color: "#93c5fd" },
            { to: "/reporting-hub", label: "Reporting Hub", icon: faChartBarIcon, color: "#38bdf8" },
            { to: "/member-database", label: "Member Database", icon: faUsers, color: "#fca5a5" },
            { to: "/category-selection", label: "Category Selections", icon: faListAlt, color: "#6ee7b7" },
            { to: "/tickets-admin", label: "Tickets-admin", icon: faTicketAlt, color: "#f9a8d4" },
          ].map((btn, i) => (
            <Link
              key={i}
              to={btn.to}
              style={{
                background: "#111b31",
                border: "1px solid rgba(147,197,253,0.18)",
                padding: "10px 18px",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "15px",
                fontWeight: 600,
                color: "#e2e8f0",
                textDecoration: "none",
              }}
            >
              <span style={{ color: btn.color }}>
                <FontAwesomeIcon icon={btn.icon} />
              </span>
              {btn.label}
            </Link>
          ))}
        </div> */}

        {/* Project list */}
        <section
          style={{
            background: "#111b31",
            border: "1px solid rgba(147,197,253,0.14)",
            borderRadius: "16px",
            padding: "20px",
            boxShadow: "0 10px 25px -15px rgba(0,0,0,0.6)",
          }}
        >
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontSize: "16px", fontWeight: "800" }}>All Projects</h2>
            <span
              style={{
                background: "rgba(147,197,253,0.1)",
                color: "#cfe0ff",
                padding: "2px 8px",
                borderRadius: "8px",
                fontSize: "12px",
              }}
            >
              {loading ? "…" : projects.length}
              {nameLoading ? " · resolving names…" : ""}
            </span>
          </header>

          {loading ? (
            <div>Loading…</div>
          ) : err ? (
            <div
              style={{
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: "8px",
                padding: "10px",
                fontSize: "14px",
                color: "#ffb4c4",
              }}
            >
              {err}
            </div>
          ) : projects.length === 0 ? (
            <div
              style={{
                background: "#0f1931",
                border: "1px dashed rgba(147,197,253,0.18)",
                padding: "20px",
                borderRadius: "10px",
                textAlign: "center",
                fontSize: "14px",
                color: "#cfe0ff",
              }}
            >
              No projects found.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {projects.map((p, idx) => {
                const pid = p.id ?? p.project_id ?? p.ID ?? idx;

                const leaderId = String(pickLeaderSliitId(p) || "").trim();
                const leaderName =
                  (leaderId &&
                    (nameMap[leaderId] ||
                      nameMap[leaderId.toUpperCase()] ||
                      nameMap[leaderId.toLowerCase()] ||
                      nameMap[leaderId.trim()])) ||
                  p.leader_name ||
                  p.created_by_name ||
                  "";
                const leaderDisplay = leaderName ? leaderName : leaderId ? leaderId : "Unknown";

                const status = (p.status || "New").toString();
                const dateStr = p.created_at ? String(p.created_at).split(" ")[0] : "";

                // FIXED: robust phase extraction with default 1
                const phaseCount = getPhaseCount(p);

                return (
                  <li key={pid} style={{ borderBottom: "1px solid rgba(147,197,253,0.12)" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 0",
                      }}
                    >
                      <div>
                        {/* Project title + Phase */}
                        <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "6px" }}>
                          Project {p.name || "Untitled"}
                          <span style={{ marginLeft: 8, fontWeight: 500, color: "#93c5fd" }}>
                            · Phase {phaseCount}
                          </span>
                        </div>

                        {/* Meta row */}
                        <div style={{ display: "flex", gap: "10px", fontSize: "12px", flexWrap: "wrap" }}>
                          <span
                            style={{
                              background: "rgba(147,197,253,0.1)",
                              border: "1px solid rgba(147,197,253,0.22)",
                              borderRadius: "6px",
                              padding: "2px 6px",
                            }}
                            title={leaderId ? `SLIITID: ${leaderId}` : undefined}
                          >
                            Leader: {leaderDisplay}
                          </span>

                          {dateStr && <span style={{ color: "#94a3b8" }}>{dateStr}</span>}

                          <span style={{ ...toneStyle(status), padding: "2px 6px", borderRadius: "6px" }}>
                            {status}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => navigate(`/project/${pid}`)}
                        style={{
                          height: "34px",
                          width: "34px",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: "8px",
                          background: "rgba(147,197,253,0.1)",
                          border: "1px solid rgba(147,197,253,0.18)",
                          cursor: "pointer",
                          color: "#e2e8f0",
                        }}
                        title="Open"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
